import { createContext, useContext, useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import i18n from '../i18n';
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_NAMES,
  getLocalizedPath,
  getPathWithoutLang,
  getLanguageFromPath,
  type SupportedLanguage,
} from '../i18n';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
  languages: typeof SUPPORTED_LANGUAGES;
  languageNames: typeof LANGUAGE_NAMES;
  getLocalizedUrl: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentLang, setCurrentLang] = useState<SupportedLanguage>(
    () => getLanguageFromPath() || 'en'
  );

  // Sync language with URL on mount and URL changes (runs only on pathname change)
  useEffect(() => {
    const urlLang = getLanguageFromPath();
    if (urlLang !== currentLang) {
      i18n.changeLanguage(urlLang);
      setCurrentLang(urlLang);
    }
  }, [location.pathname, currentLang]);

  const setLanguage = useCallback(
    (lang: SupportedLanguage) => {
      if (lang === currentLang) return;

      // Change i18n language
      i18n.changeLanguage(lang);
      setCurrentLang(lang);

      // Update URL with new language
      const currentPath = getPathWithoutLang(location.pathname);
      const search = location.search;
      const newPath = getLocalizedPath(currentPath, lang);

      navigate(newPath + search, { replace: true });

      // Store preference
      localStorage.setItem('fairday-language', lang);
    },
    [currentLang, location.pathname, location.search, navigate]
  );

  const getLocalizedUrl = useCallback(
    (path: string) => {
      return getLocalizedPath(path, currentLang);
    },
    [currentLang]
  );

  const value = useMemo<LanguageContextType>(() => ({
    language: currentLang,
    setLanguage,
    t: t as (key: string, options?: Record<string, unknown>) => string,
    languages: SUPPORTED_LANGUAGES,
    languageNames: LANGUAGE_NAMES,
    getLocalizedUrl,
  }), [currentLang, setLanguage, t, getLocalizedUrl]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Simple hook for just translation without navigation
export const useTranslate = () => {
  const { t } = useTranslation();
  return t;
};
